import { useEffect, useState } from "react";
import { ArrowLeftIcon, CheckIcon, SaveIcon } from "lucide-react";

import { Column } from "../Column";
import { Row } from "../Row";
import { Text } from "../Text";
import { Button } from "../ui/button";
import { StafferPetAdminCoins } from "./StafferPetAdminCoins";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

import { useUpsertPet } from "../../hooks/updateSets/useUpsertPet";
import { useGetPets } from "../../hooks/queries/useGetPets";
import { stafferPetShop } from "../../lib/stafferPetShop";
import { stafferPets } from "../../lib/stafferPets";
import { cn } from "../../lib/utils";
import type { Pet, User } from "../../types";

type AdminCategory = "PETS" | "COLORS" | "TOPPERS" | "ACCESSORIES";

const adminCategories: { id: AdminCategory; label: string }[] = [
  { id: "PETS", label: "Pets" },
  { id: "COLORS", label: "Colors" },
  { id: "TOPPERS", label: "Toppers" },
  { id: "ACCESSORIES", label: "Accessories" },
];

const getShopStatus = (item: unknown) => {
  if (typeof item !== "object" || item === null) return "Exclusive";
  if ("isRetired" in item && item.isRetired === true) return "Retired";
  if ("isExclusive" in item && item.isExclusive === true) return "Exclusive";
  if (!stafferPetShop.isItemAvailable(item)) return "Expired";
  if ("availableUntil" in item) return "Limited edition";
  return "In shop";
};

const getStatusClassName = (status: string) => {
  if (status === "Exclusive")
    return "border-violet-700 bg-violet-200 text-violet-950";
  if (status === "Custom")
    return "border-fuchsia-700 bg-fuchsia-200 text-fuchsia-950";
  if (status === "Retired" || status === "Expired") {
    return "border-slate-600 bg-slate-200 text-slate-800";
  }
  if (status === "Limited edition") {
    return "border-orange-700 bg-orange-200 text-orange-950";
  }
  return "border-emerald-700 bg-emerald-100 text-emerald-950";
};

export const StafferPetAdminPage = ({
  currentUserId,
  pets,
  users,
  onBack,
}: {
  currentUserId: number;
  pets: Pet[];
  users: User[];
  onBack: () => void;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: upsertPet, isPending } = useUpsertPet();
  const { refetch: refetchPets } = useGetPets();

  /* React Hooks */
  const [selectedUserId, setSelectedUserId] = useState(currentUserId);
  const [category, setCategory] = useState<AdminCategory>("PETS");
  const [draftUnlocked, setDraftUnlocked] = useState<Pet["unlocked"] | null>(
    null,
  );
  const [message, setMessage] = useState("");
  const [baseUpdatedAt, setBaseUpdatedAt] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const selectedPet = pets.find((pet) => pet.userId === selectedUserId) ?? null;
  const selectedUser = users.find((user) => user.id === selectedUserId) ?? null;

  useEffect(() => {
    setDraftUnlocked(selectedPet?.unlocked ?? null);
    setBaseUpdatedAt(selectedPet?.updatedAt ?? null);
    setMessage("");
  }, [selectedUserId, selectedPet?.userId]);

  /* Helpers */
  const getUnlockedIds = (): readonly string[] => {
    if (draftUnlocked === null) return [];
    if (category === "PETS") return draftUnlocked.petIds;
    if (category === "COLORS") return draftUnlocked.colorIds;
    if (category === "TOPPERS") return draftUnlocked.topperIds;
    return draftUnlocked.accessoryIds ?? [];
  };

  const getCatalogItems = () => {
    if (category === "PETS") {
      return stafferPets.AVAILABLE_SPECIES.filter(
        (species) => species.isRestricted,
      ).map((species) => ({
        id: species.id,
        label: species.label,
        status: getShopStatus(
          stafferPetShop.sections.PETS.items.find(
            (item) => item.id === species.id,
          ),
        ),
        src: stafferPets.getSpriteSrc(species.id),
        color: null,
      }));
    }
    if (category === "COLORS") {
      return Object.entries(stafferPets.PALETTES)
        .filter(
          ([id]) =>
            id !== "NONE" &&
            !stafferPets.isBasePaletteId(
              id as keyof typeof stafferPets.PALETTES,
            ),
        )
        .map(([id, palette]) => {
          const shopItem = stafferPetShop.sections.COLORS.items.find(
            (item) => item.id === id,
          );
          return {
            id,
            label: palette.label,
            status: getShopStatus(shopItem),
            src: null,
            color: palette.body,
          };
        });
    }
    if (category === "TOPPERS") {
      return Object.entries(stafferPets.TOPPERS).map(([id, topper]) => ({
        id,
        label: topper.label,
        status: getShopStatus(
          stafferPetShop.sections.TOPPERS.items.find((item) => item.id === id),
        ),
        src: topper.src,
        color: null,
      }));
    }
    return Object.entries(stafferPets.ACCESSORIES).map(([id, accessory]) => ({
      id,
      label: accessory.label,
      status: getShopStatus(
        stafferPetShop.sections.ACCESSORIES.items.find(
          (item) => item.id === id,
        ),
      ),
      src: accessory.src,
      color: null,
    }));
  };

  const catalogItems = getCatalogItems();
  const knownIds = new Set<string>(
    category === "PETS"
      ? stafferPets.AVAILABLE_SPECIES.map((item) => item.id)
      : category === "COLORS"
        ? Object.keys(stafferPets.PALETTES)
        : category === "TOPPERS"
          ? Object.keys(stafferPets.TOPPERS)
          : Object.keys(stafferPets.ACCESSORIES),
  );
  const items = [
    ...catalogItems,
    ...getUnlockedIds()
      .filter((id) => !knownIds.has(id))
      .map((id) => ({
        id,
        label: id.replace(/_/g, " "),
        status: "Custom",
        src: null,
        color: null,
      })),
  ].sort((a, b) => {
    const priority = (status: string) => {
      if (status === "Custom") return 0;
      if (status === "Exclusive") return 1;
      if (status === "Limited edition") return 2;
      if (status === "In shop") return 3;
      return 4;
    };
    return (
      priority(a.status) - priority(b.status) || a.label.localeCompare(b.label)
    );
  });
  const isDirty =
    selectedPet !== null &&
    draftUnlocked !== null &&
    JSON.stringify(draftUnlocked) !== JSON.stringify(selectedPet.unlocked);

  /* Handlers */
  const handleToggle = (id: string) => {
    setMessage("");
    setDraftUnlocked((current) => {
      if (current === null) return current;
      const toggle = (ids: readonly string[]) =>
        ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id];
      if (category === "PETS") {
        return {
          ...current,
          petIds: toggle(current.petIds) as Pet["unlocked"]["petIds"],
        };
      }
      if (category === "COLORS") {
        return {
          ...current,
          colorIds: toggle(current.colorIds) as Pet["unlocked"]["colorIds"],
        };
      }
      if (category === "TOPPERS") {
        return { ...current, topperIds: toggle(current.topperIds) };
      }
      return {
        ...current,
        accessoryIds: toggle(current.accessoryIds ?? []),
      };
    });
  };

  const handleSave = async () => {
    if (selectedPet === null || draftUnlocked === null || !isDirty) return;
    if (baseUpdatedAt === null) {
      setMessage(
        "Cannot save until the pet version is available. Refresh after guac deploys.",
      );
      return;
    }
    setIsChecking(true);
    let latestPet: Pet | undefined;
    try {
      const refreshed = await refetchPets();
      if (refreshed.isError) throw refreshed.error;
      latestPet = refreshed.data?.find((pet) => pet.userId === selectedUserId);
    } catch {
      setMessage("Could not refresh this pet. No changes were saved.");
      setIsChecking(false);
      return;
    }
    setIsChecking(false);
    if (!latestPet || latestPet.updatedAt !== baseUpdatedAt) {
      setMessage(
        "This pet changed since you opened it. Go back and reopen God Mode to review the latest unlocks.",
      );
      return;
    }
    const isPublicPet = stafferPets.AVAILABLE_SPECIES.some(
      (species) =>
        species.id === latestPet.equipped.petId && !species.isRestricted,
    );
    const petId =
      isPublicPet || draftUnlocked.petIds.includes(latestPet.equipped.petId)
        ? latestPet.equipped.petId
        : "FOX";
    const colorId =
      stafferPets.isBasePaletteId(latestPet.equipped.colorId) ||
      draftUnlocked.colorIds.includes(latestPet.equipped.colorId)
        ? latestPet.equipped.colorId
        : stafferPets.DEFAULT_PALETTE_BY_SPECIES[petId];
    upsertPet(
      {
        userId: latestPet.userId,
        expectedUpdatedAt: baseUpdatedAt,
        pet: {
          ...latestPet,
          unlocked: draftUnlocked,
          equipped: {
            ...latestPet.equipped,
            petId,
            colorId,
            topperId:
              latestPet.equipped.topperId !== null &&
              draftUnlocked.topperIds.includes(latestPet.equipped.topperId)
                ? latestPet.equipped.topperId
                : null,
            accessoryId:
              latestPet.equipped.accessoryId &&
              (draftUnlocked.accessoryIds ?? []).includes(
                latestPet.equipped.accessoryId,
              )
                ? latestPet.equipped.accessoryId
                : null,
            accessoryId2:
              latestPet.equipped.accessoryId2 &&
              (draftUnlocked.accessoryIds ?? []).includes(
                latestPet.equipped.accessoryId2,
              )
                ? latestPet.equipped.accessoryId2
                : null,
          },
        },
      },
      {
        onSuccess: (pet) => {
          setDraftUnlocked(pet.unlocked);
          setBaseUpdatedAt(pet.updatedAt ?? null);
          setMessage("Unlocks saved");
        },
        onError: () => {
          setMessage(
            "Save blocked. This pet may have changed; reopen God Mode to review it.",
          );
        },
      },
    );
  };

  /* Render */
  const renderItems = () => {
    if (selectedPet === null || draftUnlocked === null) {
      return (
        <Text className="border-2 border-dashed border-slate-300 p-6 font-mono text-xs text-slate-600">
          This user has not adopted a pet yet. There are no unlocks to edit.
        </Text>
      );
    }
    return (
      <Column className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => {
          const isOwned = getUnlockedIds().includes(item.id);
          return (
            <Button
              key={item.id}
              type="button"
              variant="outlineDark"
              disabled={isPending || isChecking}
              onClick={() => {
                handleToggle(item.id);
              }}
              className={cn(
                "h-20 min-w-0 justify-start gap-3 rounded-none border-2 border-slate-900 px-3 text-left font-mono shadow-[2px_2px_0_#172033]",
                isOwned ? "bg-emerald-50" : "bg-white",
              )}
            >
              {item.src ? (
                <img
                  src={item.src}
                  alt=""
                  className="h-12 w-12 shrink-0 object-contain [image-rendering:pixelated]"
                />
              ) : (
                <Column
                  className="h-10 w-10 shrink-0 border-2 border-slate-900"
                  style={{ backgroundColor: item.color ?? "#e2e8f0" }}
                />
              )}
              <Column className="min-w-0 gap-1">
                <Text className="truncate text-[10px] font-black uppercase text-slate-900">
                  {item.label}
                </Text>
                <Text
                  className={cn(
                    "w-fit border px-1.5 py-0.5 text-[9px] font-black uppercase tracking-[0.06em]",
                    getStatusClassName(item.status),
                  )}
                >
                  {item.status}
                </Text>
                <Text
                  className={cn(
                    "text-[9px] font-black uppercase",
                    isOwned ? "text-emerald-700" : "text-slate-500",
                  )}
                >
                  {isOwned
                    ? "Unlocked · click to remove"
                    : "Locked · click to grant"}
                </Text>
              </Column>
              {isOwned ? (
                <CheckIcon className="ml-auto h-4 w-4 shrink-0" />
              ) : null}
            </Button>
          );
        })}
      </Column>
    );
  };

  const render = () => {
    return (
      <>
        <Row className="items-center justify-between gap-3 border-b-4 border-slate-900 pb-3">
          <Column className="gap-0">
            <Text className="font-mono text-xl font-black uppercase text-slate-900">
              Pet God Mode
            </Text>
            <Text className="font-mono text-xs uppercase text-orange-600">
              Grant or remove a staffer’s unlocks
            </Text>
          </Column>
          <Button
            type="button"
            variant="outlineDark"
            onClick={onBack}
            className="rounded-none border-2 border-slate-900 font-mono text-xs font-black uppercase"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" /> Back
          </Button>
        </Row>
        <Column className="min-h-0 gap-4 overflow-y-auto pr-1">
          <Row className="flex-wrap items-end gap-4">
            <Column className="min-w-64 gap-1">
              <Text className="font-mono text-[10px] font-black uppercase text-slate-600">
                Staffer
              </Text>
              <Select
                value={selectedUserId.toString()}
                onValueChange={(value) => {
                  setSelectedUserId(Number(value));
                }}
              >
                <SelectTrigger className="rounded-none border-2 border-slate-900 bg-white font-mono text-xs">
                  <SelectValue placeholder="Choose a user" />
                </SelectTrigger>
                <SelectContent>
                  {users
                    .filter((user) => user.deletedAt === null)
                    .sort((a, b) =>
                      `${a.firstName} ${a.lastName}`.localeCompare(
                        `${b.firstName} ${b.lastName}`,
                      ),
                    )
                    .map((user) => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.firstName} {user.lastName} · {user.email}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </Column>
            <Text className="pb-2 font-mono text-xs text-slate-600">
              {selectedUser?.email ?? "User not found"}
            </Text>
            {selectedPet ? (
              <StafferPetAdminCoins
                key={selectedUserId}
                pet={selectedPet}
                isDisabled={isDirty || isPending || isChecking}
                onSaved={(pet) => {
                  setDraftUnlocked(pet.unlocked);
                  setBaseUpdatedAt(pet.updatedAt || null);
                }}
              />
            ) : null}
          </Row>
          <Row className="flex-wrap gap-2 border-b-2 border-dashed border-slate-300 pb-3">
            {adminCategories.map((option) => (
              <Button
                key={option.id}
                type="button"
                variant="outlineDark"
                onClick={() => {
                  setCategory(option.id);
                }}
                className={cn(
                  "rounded-none border-2 border-slate-900 px-3 font-mono text-[10px] font-black uppercase",
                  category === option.id ? "bg-amber-300" : "bg-white",
                )}
              >
                {option.label}
              </Button>
            ))}
          </Row>
          {renderItems()}
        </Column>
        <Row className="items-center justify-between gap-3 border-t-4 border-slate-900 pt-3">
          <Text className="font-mono text-xs font-bold text-slate-700">
            {message ||
              (isDirty
                ? "Unsaved changes"
                : "Select an item to change its unlock")}
          </Text>
          <Button
            type="button"
            variant="outlineDark"
            disabled={!isDirty || isPending || isChecking}
            onClick={() => {
              void handleSave();
            }}
            className="rounded-none border-2 border-slate-900 bg-emerald-300 font-mono text-xs font-black uppercase"
          >
            <SaveIcon className="mr-2 h-4 w-4" />
            {isChecking ? "Checking" : isPending ? "Saving" : "Save unlocks"}
          </Button>
        </Row>
      </>
    );
  };

  return render();
};
